// ============================================================================
// TENANT USERS HOOKS - Digital Approval System
// ============================================================================
// React hooks for tenant-scoped user operations (list + admin password reset).

import { useState, useEffect, useCallback, useMemo } from 'react';
import { tenantUserService } from 'api/services/tenantUserService';
import { tokenUtils } from 'api/config/apiConfig';
import type {
  TenantUserQueryParameters,
  TenantUserListResponse,
  AdminResetPasswordRequest,
  PaginationInfo,
  ID,
} from 'api/types';

/**
 * Hook to fetch tenant users for the current tenant (paginated).
 * Tenant ID is resolved from sessionStorage via tokenUtils.getTenantId().
 */
export const useTenantUsers = (params?: TenantUserQueryParameters) => {
  const [data, setData] = useState<TenantUserListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    const tenantId = tokenUtils.getTenantId();
    if (!tenantId) {
      setError(new Error('No tenant context available'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await tenantUserService.list(tenantId, params);
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const pagination: PaginationInfo | undefined = useMemo(() => {
    if (!data) return undefined;
    return {
      currentPage: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages,
      totalItems: data.totalCount,
      hasNextPage: data.hasNext,
      hasPreviousPage: data.hasPrevious,
    };
  }, [data]);

  return {
    data,
    items: data?.items || [],
    pagination,
    loading,
    error,
    refetch: fetchUsers,
  };
};

/**
 * Hook to reset a tenant user's password as an admin.
 * Tenant ID is resolved from sessionStorage; caller passes userId + payload.
 */
export const useAdminResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const resetPassword = async (
    userId: ID,
    payload: AdminResetPasswordRequest,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    const tenantId = tokenUtils.getTenantId();
    if (!tenantId) {
      const err = new Error('No tenant context available');
      setError(err);
      if (options?.onError) options.onError(err);
      throw err;
    }

    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      await tenantUserService.resetPassword(tenantId, userId, payload);
      setSuccess(true);
      if (options?.onSuccess) options.onSuccess();
    } catch (err) {
      const error = err as Error;
      setError(error);
      if (options?.onError) options.onError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { resetPassword, loading, error, success };
};
